import { useCallback, useEffect, useRef, useState } from 'react'
import { api, type GameRules, type Pack, type World } from '../api'

function fmtDate(iso?: string) {
  if (!iso) return 'Never'
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function fmtGamemode(gm?: string) {
  if (!gm) return '—'
  return gm.charAt(0).toUpperCase() + gm.slice(1)
}

function AddWorldModal({ onSave, onClose }: { onSave: (w: { name: string; gamemode: string; seed?: string; allowCheats: boolean }) => Promise<void>; onClose: () => void }) {
  const [name,        setName]        = useState('')
  const [gamemode,    setGamemode]    = useState('survival')
  const [seed,        setSeed]        = useState('')
  const [allowCheats, setAllowCheats] = useState(false)
  const [saving,      setSaving]      = useState(false)
  const [error,       setError]       = useState<string | null>(null)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.focus() }, [])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await onSave({ name: name.trim(), gamemode, seed: seed.trim() || undefined, allowCheats })
    } catch (e) {
      setError((e as Error).message)
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal">
        <div className="modal-header">
          <h2>Add world</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={submit}>
          <div className="form-stack">
            <div className="form-group">
              <label>World name</label>
              <input ref={ref} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Creative Build" required />
            </div>
            <div className="form-group">
              <label>Gamemode</label>
              <select value={gamemode} onChange={e => setGamemode(e.target.value)}>
                <option value="survival">Survival</option>
                <option value="creative">Creative</option>
                <option value="adventure">Adventure</option>
              </select>
            </div>
            <div className="form-group">
              <label>Seed <span className="text-muted">(optional)</span></label>
              <input value={seed} onChange={e => setSeed(e.target.value)} placeholder="Leave blank for random" />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={allowCheats}
                  onChange={e => setAllowCheats(e.target.checked)}
                  disabled={saving}
                />
                Allow cheats
                <span className="text-xs text-muted">(required for some behavior packs — disables achievements)</span>
              </label>
            </div>
            <p className="text-xs text-muted">
              Minecraft creates the world folder automatically the first time it loads.
            </p>
          </div>
          {error && <p className="error-msg mt-3">⚠ {error}</p>}
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-green" disabled={saving || !name.trim()}>
              {saving ? <><span className="spinning">◌</span> Creating…</> : 'Create world'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function CloneModal({ world, onClone, onClose }: {
  world:   World
  onClone: (name: string) => Promise<void>
  onClose: () => void
}) {
  const [name,   setName]   = useState(`${world.name} (copy)`)
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState<string | null>(null)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.focus() }, [])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await onClone(name.trim())
    } catch (e) {
      setError((e as Error).message)
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal">
        <div className="modal-header">
          <h2>Clone "{world.name}"</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={submit}>
          <div className="form-stack">
            <div className="form-group">
              <label>New world name</label>
              <input ref={ref} value={name} onChange={e => setName(e.target.value)} required />
            </div>
            <p className="text-xs text-muted">
              Copies the world folder on the server and registers it as a new entry.
              If the source world hasn't been loaded yet, the clone will be empty.
            </p>
          </div>
          {error && <p className="error-msg mt-3">⚠ {error}</p>}
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !name.trim()}>
              {saving ? <><span className="spinning">◌</span> Cloning…</> : 'Clone world'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function ManagePacksModal({ world, onClose }: { world: World; onClose: () => void }) {
  const [packs,    setPacks]    = useState<Pack[]>([])
  const [loading,  setLoading]  = useState(true)
  const [busy,     setBusy]     = useState<string | null>(null)
  const [msg,      setMsg]      = useState<{ ok: boolean; text: string } | null>(null)
  const [installed, setInstalled] = useState<{ packId: string; name: string }[]>(
    world.installedPacks ?? []
  )

  useEffect(() => {
    api.getPacks().then(p => { setPacks(p); setLoading(false) }).catch(() => setLoading(false))
  }, [])

  const installedIds = new Set(installed.map(p => p.packId))

  async function install(pack: Pack) {
    setBusy(pack.packId)
    setMsg(null)
    try {
      await api.installPack(world.worldId, pack.packId)
      setInstalled(prev => [...prev, { packId: pack.packId, name: pack.name }])
      setMsg({ ok: true, text: `"${pack.name}" installed — server restarting (~10 s).` })
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message })
    } finally {
      setBusy(null)
    }
  }

  async function remove(pack: Pack) {
    setBusy(pack.packId)
    setMsg(null)
    try {
      await api.removePack(world.worldId, pack.packId)
      setInstalled(prev => prev.filter(p => p.packId !== pack.packId))
      setMsg({ ok: true, text: `"${pack.name}" removed — server restarting (~10 s).` })
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message })
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget && !busy) onClose() }}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2>Packs — {world.name}</h2>
          <button className="modal-close" onClick={onClose} disabled={!!busy}>×</button>
        </div>

        {msg && (
          <div className="card mb-3" style={{ borderColor: msg.ok ? 'var(--green)' : 'var(--red)', padding: '10px 14px' }}>
            <p style={{ color: msg.ok ? 'var(--green)' : 'var(--red)', fontSize: '0.85rem' }}>
              {msg.ok ? '✓ ' : '✗ '}{msg.text}
            </p>
          </div>
        )}

        {loading ? (
          <p className="text-muted text-sm"><span className="spinning">◌</span> Loading packs…</p>
        ) : packs.length === 0 ? (
          <p className="text-muted text-sm">No packs in library. Upload packs from the Packs tab first.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {packs.map(p => {
              const isOn  = installedIds.has(p.packId)
              const isBusy = busy === p.packId
              return (
                <div key={p.packId} className="flex items-center gap-3" style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="flex items-center gap-2">
                      <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{p.name}</span>
                      {p.components.map((c, i) => (
                        <span key={i} className={`badge ${c.type === 'resources' ? 'badge-green' : 'badge-gray'}`} style={{ fontSize: '0.7rem' }}>
                          {c.type === 'resources' ? 'Resource' : 'Behavior'}
                        </span>
                      ))}
                    </div>
                    {p.description && <p className="text-xs text-muted mt-1">{p.description}</p>}
                  </div>
                  <button
                    className={`btn btn-sm ${isOn ? 'btn-danger' : 'btn-green'}`}
                    style={{ flexShrink: 0 }}
                    disabled={!!busy}
                    onClick={() => isOn ? remove(p) : install(p)}
                  >
                    {isBusy
                      ? <><span className="spinning">◌</span> {isOn ? 'Removing…' : 'Installing…'}</>
                      : isOn ? 'Remove' : 'Install'}
                  </button>
                </div>
              )
            })}
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={!!busy}>Close</button>
        </div>
      </div>
    </div>
  )
}

const GAME_RULE_DEFAULTS: Required<GameRules> = {
  showcoordinates:      false,
  keepinventory:        false,
  pvp:                  true,
  mobgriefing:          true,
  dofiretick:           true,
  dodaynightcycle:      true,
  naturalregeneration:  true,
  commandblocksenabled: false,
  playerwaypoints:      true,
}

const RULE_META: { key: keyof GameRules; label: string; desc: string }[] = [
  { key: 'showcoordinates',      label: 'Show Coordinates',     desc: 'Players can see their XYZ position' },
  { key: 'keepinventory',        label: 'Keep Inventory',       desc: 'Keep items and XP on death' },
  { key: 'pvp',                  label: 'PvP',                  desc: 'Players can damage each other' },
  { key: 'mobgriefing',          label: 'Mob Griefing',         desc: 'Creepers and endermen destroy blocks' },
  { key: 'dofiretick',           label: 'Fire Spread',          desc: 'Fire spreads to nearby blocks' },
  { key: 'dodaynightcycle',      label: 'Day/Night Cycle',      desc: 'Time advances normally' },
  { key: 'naturalregeneration',  label: 'Natural Regeneration', desc: 'Health regenerates over time' },
  { key: 'commandblocksenabled', label: 'Command Blocks',       desc: 'Command blocks can execute' },
  { key: 'playerwaypoints',      label: 'Locator Bar',          desc: 'Show other players on the locator bar' },
]

function GameRulesModal({ world, onSave, onClose }: {
  world:   World
  onSave:  (updated: World) => void
  onClose: () => void
}) {
  const rules   = { ...GAME_RULE_DEFAULTS, ...(world.gameRules ?? {}) }
  const [draft,  setDraft]  = useState<Required<GameRules>>(rules)
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState<string | null>(null)
  const [saved,  setSaved]  = useState(false)

  function toggle(key: keyof GameRules) {
    setDraft(prev => ({ ...prev, [key]: !prev[key] }))
    setSaved(false)
  }

  async function save() {
    setSaving(true)
    setError(null)
    try {
      const updated = await api.updateWorldSettings(world.worldId, { gameRules: draft })
      setSaved(true)
      onSave(updated)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const isActive = world.activeOnInstance2 === true

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget && !saving) onClose() }}>
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2>Game Rules — {world.name}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}>×</button>
        </div>

        <p className="text-xs text-muted mb-3" style={{ marginTop: '-4px' }}>
          {isActive
            ? 'This world is active on Instance 2. Changes apply immediately to the running server.'
            : 'Changes save now and apply the next time this world is loaded on Instance 2.'}
        </p>

        <div className="flex flex-col" style={{ gap: 2 }}>
          {RULE_META.map(({ key, label, desc }) => {
            const on = draft[key]
            return (
              <div
                key={key}
                className="flex items-center"
                style={{ padding: '8px 4px', borderBottom: '1px solid var(--border)', gap: 12 }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{label}</div>
                  <div className="text-xs text-muted">{desc}</div>
                </div>
                <button
                  className={`btn btn-sm ${on ? 'btn-green' : 'btn-ghost'}`}
                  style={{ minWidth: 52, flexShrink: 0 }}
                  onClick={() => toggle(key)}
                  disabled={saving}
                >
                  {on ? 'ON' : 'OFF'}
                </button>
              </div>
            )
          })}
        </div>

        {error && <p className="error-msg mt-3">⚠ {error}</p>}
        {saved && !error && (
          <p className="text-xs mt-3" style={{ color: 'var(--green)' }}>
            ✓ {isActive ? 'Rules applied to running server.' : 'Rules saved — apply on next world load.'}
          </p>
        )}

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Close</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <><span className="spinning">◌</span> Saving…</> : 'Save rules'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Worlds() {
  const [worlds,       setWorlds]       = useState<World[]>([])
  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState<string | null>(null)
  const [switching,    setSwitching]    = useState<string | null>(null)
  const [deleting,     setDeleting]     = useState<string | null>(null)
  const [backing,      setBacking]      = useState<string | null>(null)
  const [cloning,      setCloning]      = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<World | null>(null)
  const [cloneTarget,  setCloneTarget]  = useState<World | null>(null)
  const [switchMsg,    setSwitchMsg]    = useState<{ ok: boolean; text: string } | null>(null)
  const [actionMsg,    setActionMsg]    = useState<{ ok: boolean; text: string } | null>(null)
  const [showAdd,      setShowAdd]      = useState(false)
  const [packTarget,    setPackTarget]    = useState<World | null>(null)
  const [rulesTarget,   setRulesTarget]   = useState<World | null>(null)
  const [togglingCheats, setTogglingCheats] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const data = await api.getWorlds()
      setWorlds(data)
      setError(null)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function deleteWorld(w: World, deleteFiles: boolean) {
    setDeleteTarget(null)
    setDeleting(w.worldId)
    try {
      await api.deleteWorld(w.worldId, deleteFiles)
      load()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setDeleting(null)
    }
  }

  async function switchWorld(w: World) {
    setSwitching(w.worldId)
    setSwitchMsg(null)
    try {
      const result = await api.switchWorld(w.worldId)
      setSwitchMsg({ ok: true, text: `Switched to "${result.name}" — Instance 2 is restarting.` })
      load()
    } catch (e) {
      setSwitchMsg({ ok: false, text: (e as Error).message })
    } finally {
      setSwitching(null)
    }
  }

  async function backupWorld(w: World) {
    setBacking(w.worldId)
    setActionMsg(null)
    try {
      const result = await api.backupWorld(w.worldId)
      setActionMsg({ ok: true, text: `Backup saved: ${result.backupFile} (${result.size})` })
    } catch (e) {
      setActionMsg({ ok: false, text: (e as Error).message })
    } finally {
      setBacking(null)
    }
  }

  async function cloneWorld(w: World, name: string) {
    setCloning(w.worldId)
    setCloneTarget(null)
    setActionMsg(null)
    try {
      const result = await api.cloneWorld(w.worldId, name)
      setActionMsg({
        ok:   true,
        text: result.sourceWasMissing
          ? `"${result.name}" registered (source folder not yet on server — will be populated on first load).`
          : `"${result.name}" cloned successfully.`,
      })
      load()
    } catch (e) {
      setActionMsg({ ok: false, text: (e as Error).message })
    } finally {
      setCloning(null)
    }
  }

  async function toggleCheats(w: World) {
    setTogglingCheats(w.worldId)
    try {
      const updated = await api.updateWorldSettings(w.worldId, { allowCheats: !w.allowCheats })
      setWorlds(prev => prev.map(x => x.worldId === w.worldId ? { ...x, allowCheats: updated.allowCheats } : x))
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setTogglingCheats(null)
    }
  }

  const busy = switching !== null || deleting !== null || backing !== null || cloning !== null || togglingCheats !== null

  return (
    <>
      <div className="page-header flex items-center justify-between">
        <div>
          <h1>Worlds</h1>
          <p>Manage worlds loaded on Instance 2. Instance 1 is always "Bedrock level".</p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
            {loading ? <span className="spinning">◌</span> : '↻'} Refresh
          </button>
          <button className="btn btn-green btn-sm" onClick={() => setShowAdd(true)}>
            + Add world
          </button>
        </div>
      </div>

      {switchMsg && (
        <div className="card mb-4" style={{ borderColor: switchMsg.ok ? 'var(--green)' : 'var(--red)' }}>
          <p style={{ color: switchMsg.ok ? 'var(--green)' : 'var(--red)', fontSize: '0.875rem' }}>
            {switchMsg.ok ? '✓ ' : '✗ '}{switchMsg.text}
          </p>
          {switchMsg.ok && (
            <p className="text-xs text-muted mt-2">
              The server takes ~20 s to restart. Check the Dashboard for status.
            </p>
          )}
        </div>
      )}

      {actionMsg && (
        <div className="card mb-4" style={{ borderColor: actionMsg.ok ? 'var(--green)' : 'var(--red)' }}>
          <p style={{ color: actionMsg.ok ? 'var(--green)' : 'var(--red)', fontSize: '0.875rem' }}>
            {actionMsg.ok ? '✓ ' : '✗ '}{actionMsg.text}
          </p>
        </div>
      )}

      {error && <p className="error-msg mb-4">⚠ {error}</p>}

      {loading && worlds.length === 0 ? (
        <p className="text-muted text-sm">Loading…</p>
      ) : (
        <div className="world-list">
          {worlds.map(w => (
            <div key={w.worldId} className={`world-card ${w.permanent ? 'permanent' : ''}`}>
              <div style={{ fontSize: '1.4rem' }}>{w.permanent ? '🌍' : '🗺'}</div>

              <div className="world-info">
                <div className="flex items-center gap-2">
                  <span className="world-name">{w.name}</span>
                  {w.permanent && <span className="badge badge-green">permanent</span>}
                  {w.clonedFrom && <span className="badge badge-gray">clone</span>}
                  {w.allowCheats && <span className="badge" style={{ background: 'rgba(251,146,60,0.15)', color: '#fb923c', border: '1px solid #fb923c' }}>cheats on</span>}
                </div>
                <div className="world-meta">
                  {fmtGamemode(w.gamemode)} · Last played: {fmtDate(w.lastPlayed)}
                  {w.levelName && w.levelName !== w.name && (
                    <> · <span className="font-mono">{w.levelName}</span></>
                  )}
                </div>
                {(w.installedPacks ?? []).length > 0 && (
                  <div className="flex gap-1 mt-1" style={{ flexWrap: 'wrap' }}>
                    {(w.installedPacks ?? []).map(p => (
                      <span key={p.packId} className="badge badge-gray" style={{ fontSize: '0.7rem' }}>
                        📦 {p.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {!w.permanent && (
                <div className="flex gap-2" style={{ flexShrink: 0 }}>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => switchWorld(w)}
                    disabled={busy}
                  >
                    {switching === w.worldId ? (
                      <><span className="spinning">◌</span> Switching…</>
                    ) : 'Load on Instance 2'}
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setCloneTarget(w)}
                    disabled={busy}
                    title="Clone world"
                  >
                    {cloning === w.worldId ? <span className="spinning">◌</span> : 'Clone'}
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => backupWorld(w)}
                    disabled={busy}
                    title="Back up world folder to server"
                  >
                    {backing === w.worldId ? <span className="spinning">◌</span> : 'Backup'}
                  </button>
                  <button
                    className="btn btn-sm"
                    style={w.allowCheats
                      ? { background: 'rgba(251,146,60,0.15)', color: '#fb923c', border: '1px solid #fb923c' }
                      : {}}
                    onClick={() => toggleCheats(w)}
                    disabled={busy}
                    title={w.allowCheats ? 'Cheats on — click to disable' : 'Cheats off — click to enable'}
                  >
                    {togglingCheats === w.worldId
                      ? <span className="spinning">◌</span>
                      : w.allowCheats ? '⚡ Cheats' : 'Cheats'}
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setPackTarget(w)}
                    disabled={busy}
                    title="Manage packs"
                  >
                    Packs
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setRulesTarget(w)}
                    disabled={busy}
                    title="Game rule toggles"
                  >
                    Rules
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => setDeleteTarget(w)}
                    disabled={busy}
                    title="Delete world"
                  >
                    {deleting === w.worldId ? <span className="spinning">◌</span> : '✕'}
                  </button>
                </div>
              )}
            </div>
          ))}

          {worlds.length === 0 && (
            <p className="text-muted text-sm">No worlds found.</p>
          )}
        </div>
      )}

      {showAdd && (
        <AddWorldModal
          onSave={async (w) => {
            await api.createWorld(w)
            setShowAdd(false)
            load()
          }}
          onClose={() => setShowAdd(false)}
        />
      )}


      {packTarget && (
        <ManagePacksModal
          world={packTarget}
          onClose={() => setPackTarget(null)}
        />
      )}

      {rulesTarget && (
        <GameRulesModal
          world={rulesTarget}
          onSave={updated => {
            setWorlds(prev => prev.map(w => w.worldId === updated.worldId ? { ...w, gameRules: updated.gameRules } : w))
            setRulesTarget(prev => prev ? { ...prev, gameRules: updated.gameRules } : null)
          }}
          onClose={() => setRulesTarget(null)}
        />
      )}

      {cloneTarget && (
        <CloneModal
          world={cloneTarget}
          onClone={async (name) => {
            await cloneWorld(cloneTarget, name)
          }}
          onClose={() => setCloneTarget(null)}
        />
      )}

      {deleteTarget && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setDeleteTarget(null) }}>
          <div className="modal">
            <div className="modal-header">
              <h2>Delete "{deleteTarget.name}"?</h2>
              <button className="modal-close" onClick={() => setDeleteTarget(null)}>×</button>
            </div>
            <p className="text-sm text-muted">Choose how much to delete:</p>
            <div className="flex flex-col gap-3 mt-3">
              <button
                className="btn btn-ghost w-full"
                style={{ justifyContent: 'flex-start', padding: '12px 16px' }}
                onClick={() => deleteWorld(deleteTarget, false)}
              >
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text)' }}>Remove from list only</div>
                  <div className="text-xs text-muted mt-1">World files stay on the server — can be re-added anytime</div>
                </div>
              </button>
              <button
                className="btn btn-danger w-full"
                style={{ justifyContent: 'flex-start', padding: '12px 16px' }}
                onClick={() => deleteWorld(deleteTarget, true)}
              >
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontWeight: 600 }}>Delete permanently</div>
                  <div className="text-xs mt-1" style={{ opacity: 0.8 }}>Removes from list AND deletes world folder from server — cannot be undone</div>
                </div>
              </button>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteTarget(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
